import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DASHBOARD_BASE } from "../../../../../config/featureFlags";
import { ArrowLeft, Calendar } from "lucide-react";
import { Breadcrumb } from "../../../components/Breadcrumb";
import { ClassicMotion } from "../../../components/classic/micro";
import { WowMotion } from "../WowMotion";
import { inks, paper } from "../viz";

export function isGraphicalDrill() {
  return typeof window !== "undefined" && window.location.pathname.includes("/graphical");
}

/** "Aug 22 – Aug 22" said the same thing twice; collapse it to "Aug 22". */
function collapseSpan(label) {
  if (typeof label !== "string") return label;
  const parts = label.split(/\s+[–-]\s+/);
  if (parts.length === 2 && parts[0].trim() === parts[1].trim()) return parts[0].trim();
  return label;
}

/**
 * Compact scope line: the old ScopeBar paragraph as a chip + one short
 * clause, with the full wording preserved in the hover title. Same shield,
 * a tenth of the ink.
 */
function ScopeLine({ t, subject = [], excludes = [], coverage }) {
  const k = inks(t);
  const ppr = paper(t);
  const subj = subject.filter(Boolean);
  if (!subj.length) return null;
  const ex = excludes.filter(Boolean);
  const full = `Every figure on this page is limited to ${subj.join(" › ")}.`
    + (ex.length ? ` Excluded: ${ex.join(", ")} — nothing outside this drill appears here.` : "")
    + (coverage ? ` ${coverage}` : "");
  return (
    <div
      title={full}
      style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", fontSize: 12.5, color: t.c.textTertiary }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11.5, fontWeight: 650, color: k.blue,
        background: t.c.primarySoft, boxShadow: `0 0 0 1px ${k.blue}44`,
        borderRadius: 999, padding: "3px 11px",
      }}>
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ width: 12, height: 12, flexShrink: 0 }}>
          <path d="M8 1.5 2 4v3.6c0 3.4 2.4 6.5 6 6.9 3.6-.4 6-3.5 6-6.9V4L8 1.5Z"
            stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M5.6 8 7.3 9.7l3.2-3.4" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {subj[subj.length - 1]} only
      </span>
      <span>
        {ex.length ? `Excluded: ${ex.join(", ")}.` : "Nothing outside this drill appears here."}
        {coverage ? ` ${coverage}` : ""}
      </span>
      <span aria-hidden="true" style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 15, height: 15, borderRadius: 99, flexShrink: 0,
        border: `1px solid ${t.c.border}`, background: ppr.card,
        fontSize: 9.5, fontWeight: 700, color: t.c.textTertiary, cursor: "help",
      }}>i</span>
    </div>
  );
}

/**
 * One chip vocabulary for every drill head (2026-08-30). The leading chip
 * carries the page's tone as a dot; a chip may pass `{ label, tone }` to
 * colour itself (verdicts), or a plain string.
 */
export function HeadChip({ t, children, tone, dot }) {
  const ppr = paper(t);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
      fontSize: 12.5, fontWeight: 650, padding: "6px 13px", borderRadius: 999,
      background: tone ? `${tone}1f` : ppr.well,
      border: `1px solid ${tone ? `${tone}55` : t.c.border}`,
      color: tone || t.c.textSecondary, fontVariantNumeric: "tabular-nums",
    }}>
      {dot ? <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 99, background: dot, flexShrink: 0 }} /> : null}
      {children}
    </span>
  );
}

/**
 * Shared chrome for graphical drills. Layer names stay internal — never on screen.
 *
 * 2026-08-30 — ONE head anatomy for every level (person, project, tool,
 * contributor, theme, stretch, sitting), the same one the Work Summary
 * drawer uses: tone rail · icon tile · kicker · title · chips. Nothing in
 * the head is grey-on-grey: the tile, the rail, the kicker and the leading
 * chip all carry the page's ink.
 *
 *   tone      ink name (blue | violet | gold | pink | teal) — the page's colour
 *   icon      what goes in the 46px tile: <ToolIcon>, <Avatar>, a lucide glyph
 *   titleIcon legacy alias for `icon`
 *   chips     [string | { label, tone, dot }] rendered under the title
 *   sentence  optional prose under the title (replaces chips when set)
 *   readings  optional [{ icon, value, label, tone }] row under the hero
 */
export function DrillFrame({
  t, title, titleIcon, icon, subtitle, crumbs = [], eyebrow = "TeamGrid · work picture",
  tone = "blue", chips = [], sentence, readings,
  backTo, backLabel = "Back", subject, excludes, coverage, instrument, children,
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Opened from the Brain orbit (?from=brain): the back button returns to the
  // Brain, not to the classic/graphical dashboard the path would imply.
  const backTarget = params.get("from") === "brain" ? `${DASHBOARD_BASE}/brain` : backTo;
  const k = inks(t);
  const ppr = paper(t);
  const when = collapseSpan(subtitle);
  const hue = k[tone] || k.blue;
  const tile = icon || titleIcon || null;
  const chipList = (chips || []).filter(Boolean).map((c) => (typeof c === "string" ? { label: c } : c));
  const readList = [];
  const seenRead = new Set();
  for (const r of readings || []) {
    if (!r) continue;
    const sig = `${r.value}\0${typeof r.label === "string" || typeof r.label === "number" ? r.label : ""}`;
    if (seenRead.has(sig)) continue;
    seenRead.add(sig);
    readList.push(r);
    if (readList.length >= 3) break;
  }

  return (
    <div style={{
      minHeight: "100%", background: ppr.canvas, padding: "0 0 80px",
      fontFamily: t.font.family, color: t.c.text,
    }}>
      <ClassicMotion />
      <WowMotion />
      <div style={{
        position: "sticky", top: 0, zIndex: 40,
        background: ppr.canvas,
        padding: "12px 26px 10px",
        borderBottom: `1px solid ${t.c.border}`,
        boxShadow: `0 8px 20px -16px ${t.c.shadowColor || "rgba(24,32,46,.28)"}`,
      }}>
        <div style={{
          maxWidth: t.layout.contentMax, margin: "0 auto",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <button
            type="button"
            onClick={() => navigate(backTarget)}
            aria-label={backLabel}
            title={backLabel}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: 11, color: "#fff",
              border: "none", background: hue, cursor: "pointer",
              fontFamily: t.font.family, flexShrink: 0, padding: 0,
            }}
          >
            <ArrowLeft size={16} strokeWidth={2.2} />
          </button>
          <div style={{ minWidth: 0, flex: 1 }}><Breadcrumb items={params.get("from") === "brain" && crumbs.length ? [{ label: "Brain", to: `${DASHBOARD_BASE}/brain` }, ...crumbs.slice(1)] : crumbs} /></div>
          {when && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0,
              fontSize: 13, fontWeight: 650, color: t.c.text,
              padding: "8px 13px", borderRadius: 12, background: ppr.card, border: `1px solid ${t.c.border}`,
            }}>
              <Calendar size={14} strokeWidth={2.2} color={t.c.textSecondary} />
              {when}
            </span>
          )}
        </div>
      </div>
      <div style={{ maxWidth: t.layout.contentMax, margin: "0 auto", padding: "16px 26px 0" }}>
        {/* ── the head — same anatomy at every level ─────────────────────── */}
        <div className="tgw-a" style={{
          position: "relative", overflow: "hidden", marginBottom: 14,
          padding: readList.length ? "18px 20px 16px" : "20px 24px 18px",
          borderRadius: t.r.hero,
          background: ppr.card, border: `1px solid ${t.c.border}`, boxShadow: t.shadow.raised,
        }}>
          {readList.length ? null : (
            <span aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: hue }} />
          )}
          <span aria-hidden="true" style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: `radial-gradient(560px 170px at 96% -50%, ${hue}${t.isDark ? "22" : "18"}, transparent 62%)`,
          }} />
          <div style={{
            position: "relative",
            display: readList.length ? "grid" : "flex",
            gridTemplateColumns: readList.length ? "minmax(240px, 1fr) minmax(0, 1.45fr)" : undefined,
            gap: readList.length ? 22 : 16,
            alignItems: readList.length ? "center" : "flex-start",
          }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: 0 }}>
              {tile ? (
                <span style={{
                  width: readList.length ? 58 : 50, height: readList.length ? 58 : 50,
                  borderRadius: 16, flex: "none",
                  display: "grid", placeItems: "center", overflow: "hidden",
                  background: `${hue}${t.isDark ? "26" : "18"}`, border: `1px solid ${hue}${t.isDark ? "48" : "3a"}`,
                  color: hue, boxShadow: `0 6px 16px -10px ${hue}`,
                }}>{tile}</span>
              ) : null}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: 12.5, fontWeight: 650, letterSpacing: "-0.1px",
                  color: hue,
                }}>{eyebrow}</div>
                <h1 style={{
                  margin: "4px 0 0", fontSize: readList.length ? 28 : 27, fontWeight: 750,
                  letterSpacing: "-0.8px", lineHeight: 1.15,
                  minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                }}>{title}</h1>
                {sentence ? (
                  <p style={{ margin: "8px 0 0", fontSize: 14.5, lineHeight: 1.5, maxWidth: "52ch", color: t.c.textSecondary }}>{sentence}</p>
                ) : chipList.length > 0 ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {chipList.map((c, i) => (
                      <HeadChip key={i} t={t} tone={c.tone} dot={c.dot || (i === 0 && !c.tone ? hue : null)}>{c.label}</HeadChip>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            {readList.length > 0 ? (
              <div
                key={readList.map((r) => r.value).join("|")}
                style={{
                display: "grid",
                gridTemplateColumns: `repeat(${readList.length}, minmax(0, 1fr))`,
                gap: 8, padding: 7, borderRadius: 16,
                background: ppr.well, border: `1px solid ${t.c.border}`,
              }}>
                {readList.map((r, i) => {
                  const ink = r.tone ? (k[r.tone] || r.tone) : hue;
                  return (
                    <div key={`read-${i}-${r.value}`} style={{
                      display: "flex", gap: 10, alignItems: "center",
                      padding: "11px 12px", borderRadius: 12,
                      background: t.isDark ? ppr.card : "#fff",
                      border: `1px solid ${t.c.border}`,
                      boxShadow: `0 1px 2px ${t.c.shadowColor}`,
                    }}>
                      {r.icon ? (
                        <span aria-hidden="true" style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          display: "grid", placeItems: "center",
                          background: ink, color: "#fff",
                          boxShadow: `0 5px 12px -6px ${ink}`,
                        }}>{r.icon}</span>
                      ) : null}
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: 20, fontWeight: 750, letterSpacing: "-0.6px", lineHeight: 1,
                          fontVariantNumeric: "tabular-nums",
                        }}>{r.value}</div>
                        <div style={{
                          marginTop: 4, fontSize: 11.5, color: t.c.textSecondary,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{r.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        {subject?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <ScopeLine t={t} subject={subject} excludes={excludes} coverage={coverage} />
          </div>
        )}

        {instrument ? (
          <div className="tgw-a tgw-a1" style={{ marginBottom: 18 }}>
            {instrument}
          </div>
        ) : null}

        {children}
      </div>
    </div>
  );
}

export function DrillHero({ t, eyebrow, value, caption, right, children }) {
  const k = inks(t);
  const ppr = paper(t);
  return (
    <div className="tgw-a tgw-a1" style={{
      display: "grid", gridTemplateColumns: right ? "1fr auto" : "1fr",
      gap: 16, alignItems: "end", padding: "22px 24px", borderRadius: t.r.hero,
      background: ppr.card, border: `1px solid ${t.c.border}`,
      boxShadow: t.shadow.raised,
      marginBottom: 14,
    }}>
      <div>
        <div style={{ ...t.font.statLabel, color: k.blue }}>
          {eyebrow}
        </div>
        <div style={{
          fontSize: 56, fontWeight: 680, letterSpacing: "-2.4px", lineHeight: 1.05,
          margin: "8px 0 6px", fontVariantNumeric: "tabular-nums",
        }}>
          {value}
        </div>
        {caption && <div style={{ fontSize: 13, color: t.c.textSecondary }}>{caption}</div>}
        {children}
      </div>
      {right}
    </div>
  );
}

export function ClickRow({ t, onClick, children }) {
  return (
    <button
      type="button"
      className="tgw-row"
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%",
        textAlign: "left", border: 0, background: "none", cursor: "pointer",
        padding: "10px 8px", margin: "0 -8px", fontFamily: t.font.family, color: "inherit",
      }}
    >
      {children}
      <span style={{ color: t.c.textTertiary, fontSize: 16, marginLeft: "auto" }}>›</span>
    </button>
  );
}

export default DrillFrame;
